import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo, CrescentBg } from "@/components/layout/Logo";
import { initGoogleLogin } from "@/api/google-auth";

const isCapacitor = typeof (window as unknown as Record<string, unknown>).Capacitor !== "undefined";

interface AuthScreenProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (name: string, email: string, password: string) => Promise<void>;
  onGoogleLogin: (idToken: string) => void;
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z" fill="#4285F4" />
      <path d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 01-7.18-2.54H1.83v2.07A8 8 0 008.98 17z" fill="#34A853" />
      <path d="M4.5 10.52a4.8 4.8 0 010-3.04V5.41H1.83a8 8 0 000 7.18l2.67-2.07z" fill="#FBBC05" />
      <path d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 001.83 5.4L4.5 7.49a4.77 4.77 0 014.48-3.3z" fill="#EA4335" />
    </svg>
  );
}

export function AuthScreen({ onLogin, onRegister, onGoogleLogin }: AuthScreenProps) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [gsiReady, setGsiReady] = useState(false);

  useEffect(() => {
    // Google Identity Services only works in real browsers, not in Capacitor WebView
    if (isCapacitor) return;
    if (googleBtnRef.current) {
      initGoogleLogin(
        (idToken) => onGoogleLogin(idToken),
        googleBtnRef.current,
      ).then(() => setGsiReady(true))
        .catch(() => { /* fallback button shown */ });
    }
  }, [onGoogleLogin]);

  const handleGoogleFallback = () => {
    // OAuth popup flow for Capacitor / fallback
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const redirectUri = isCapacitor
      ? "https://ehille.org/auth/google/callback"
      : `${window.location.origin}/auth/google/callback`;
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&prompt=select_account`;
    window.open(url, "_blank");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await onLogin(email, password);
      } else {
        await onRegister(name, email, password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bir hata olustu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-ehille-dark via-ehille-dark2 to-[#1a1f2e] relative overflow-hidden">
      {/* Decorative crescents */}
      <div className="absolute -top-5 -right-8 text-white">
        <CrescentBg size={200} opacity={0.07} />
      </div>
      <div className="absolute top-20 -left-10 text-white">
        <CrescentBg size={160} opacity={0.04} />
      </div>

      {/* Branding */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 pt-16 pb-6 relative z-10">
        <Logo size={68} />
        <h1 className="mt-4 font-display text-4xl font-bold text-white tracking-widest">
          ehille
        </h1>
        <p className="mt-3 text-muted-foreground text-sm text-center leading-relaxed" dir="rtl">
          يَسْـَٔلُونَكَ عَنِ الْاَهِلَّةِ
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/60 italic text-center max-w-[260px]">
          "O, insanlar ve hacc icin bir zaman olcusudur" - Bakara 189
        </p>
      </div>

      {/* Auth card */}
      <div className="bg-card rounded-t-[28px] px-6 pt-7 pb-10 shadow-[0_-10px_40px_rgba(0,0,0,0.2)] relative z-10">
        {/* Mode toggle */}
        <div className="flex bg-muted rounded-2xl p-1 mb-6">
          {(["login", "register"] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 py-2.5 rounded-xl text-[13px] font-bold transition-all ${
                mode === m
                  ? "bg-ehille-dark text-white shadow-md"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {m === "login" ? "Giris Yap" : "Kayit Ol"}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <Input
              label="Ad Soyad"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Adinizi girin"
              required
            />
          )}
          <Input
            label="E-posta"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ornek@email.com"
            required
          />
          <Input
            label="Sifre"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="En az 8 karakter"
            minLength={8}
            required
          />

          {error && (
            <p className="text-ehille-red text-sm text-center font-medium">{error}</p>
          )}

          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading
              ? "Yukleniyor..."
              : mode === "login"
                ? "Giris Yap"
                : "Hesap Olustur"}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center my-5">
          <div className="flex-1 h-px bg-border" />
          <span className="px-3 text-[11px] text-muted-foreground">veya</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Google Sign-In */}
        {/* GSI rendered button (works in browser) */}
        <div ref={googleBtnRef} className={`w-full flex justify-center ${gsiReady ? "" : "hidden"}`} />
        {/* Fallback button (for Capacitor / when GSI fails) */}
        {!gsiReady && (
          <Button
            variant="outline"
            size="lg"
            className="w-full gap-3"
            onClick={handleGoogleFallback}
          >
            <GoogleIcon />
            Google ile devam et
          </Button>
        )}
      </div>
    </div>
  );
}
